"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRightCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { BlockProps } from "../BlocksManager/registry";

const variantStyles: any = {
  jaune: {
    card: "bg-yellow-light border-4 border-yellow-light hover:border-yellow",
    bubble: "bg-yellow",
    button: "bg-yellow",
  },
  orange: {
    card: "bg-orange-light border-4 border-orange-light hover:border-orange",
    bubble: "bg-orange",
    button: "bg-orange",
  },
  verte: {
    card: "bg-green-light border-4 border-green-light hover:border-green",
    bubble: "bg-green",
    button: "bg-green",
  },
};

const AUTO_SLIDE_DELAY = 3500;

export default function NosCibles({ data }: BlockProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mobileHeight, setMobileHeight] = useState<number>(0);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  if (!data || !data.cibles?.length) return null;

  useEffect(() => {
    if (data.cibles.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % data.cibles.length);
    }, AUTO_SLIDE_DELAY);

    return () => clearInterval(interval);
  }, [data.cibles.length]);

  useEffect(() => {
    const updateHeight = () => {
      const activeSlide = slideRefs.current[currentIndex];
      if (activeSlide) {
        setMobileHeight(activeSlide.offsetHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, [currentIndex, data.cibles]);

  return (
    <section className="bg-violet-light py-12 " key={data.id}>
      <div className="container w-full">
        <h2 className="font-display mb-10 text-[36px] font-bold leading-[1.2] text-text-body lg:text-[50px]">
          {data.titre}
        </h2>

        {/* Mobile / tablette : slider automatique avec hauteur dynamique */}
        <div className="xl:hidden">
          <div
            className="overflow-hidden transition-[height] duration-500 ease-in-out"
            style={{ height: mobileHeight ? `${mobileHeight}px` : "auto" }}
          >
            <div
              className="flex items-start transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {data.cibles.map((item: any, index: number) => {
                const variante = item.variante?.toLowerCase?.().trim();
                const style = variantStyles[variante] || variantStyles.jaune;
                const iconUrl = item.icon?.url;

                return (
                  <div
                    key={item.id}
                    ref={(el) => {
                      slideRefs.current[index] = el;
                    }}
                    className="w-full shrink-0 px-1"
                  >
                    <article
                      className={`relative overflow-hidden rounded-[25px] px-6 pb-8 pt-8 shadow-sm ${style.card}`}
                    >
                      <div
                        className={`absolute left-6 top-10 h-24 w-24 rounded-full ${style.bubble}`}
                      />
                      <div
                        className={`absolute left-20 top-10 h-8 w-8 rounded-full ${style.bubble}`}
                      />
                      <div
                        className={`absolute -right-16 top-20 h-44 w-44 rounded-full ${style.bubble}`}
                      />
                      <div
                        className={`absolute bottom-20 -left-6 h-14 w-14 rounded-full ${style.bubble}`}
                      />

                      <div className="relative z-10 flex flex-col">
                        <div className="mb-10">
                          <div className="relative h-[72px] w-[72px]">
                            {iconUrl && (
                              <Image
                                src={iconUrl}
                                alt={item.icon?.alternativeText || item.titre}
                                fill
                                className="object-contain"
                              />
                            )}
                          </div>
                        </div>

                        <h3 className="text-[24px] font-bold leading-[1.15] text-text-body">
                          {item.titre}
                        </h3>

                        <p className="mt-6 text-[18px] leading-[1.35] text-black">
                          {item.description}
                        </p>

                        <div className="mt-8">
                          <Link
                            href={item.cta?.link || "#"}
                            className={`inline-flex items-center gap-3 rounded-full px-6 py-3 text-[16px] font-semibold text-black transition ${style.button}`}
                          >
                            {item.cta?.text}
                            <ArrowRightCircle className="h-5 w-5" />
                          </Link>
                        </div>
                      </div>
                    </article>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dots */}
          {data.cibles.length > 1 && (
            <div className="mt-6 flex justify-center gap-2">
              {data.cibles.map((_: any, index: number) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  className={`h-3 w-3 rounded-full transition ${
                    currentIndex === index ? "bg-pink" : "bg-blue/30"
                  }`}
                  aria-label={`Aller à la slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Desktop : grille normale */}
        <div className="hidden gap-6 xl:grid xl:grid-cols-3">
          {data.cibles.map((item: any) => {
            const variante = item.variante?.toLowerCase?.().trim();
            const style = variantStyles[variante] || variantStyles.jaune;
            const iconUrl = item.icon?.url;

            return (
              <article
                key={item.id}
                className={`relative min-h-[500px] overflow-hidden rounded-[25px] px-8 pb-8 pt-10 hover:shadow-xl ${style.card}`}
              >
                <div
                  className={`absolute left-10 top-12 h-27.5 w-27.5 rounded-full ${style.bubble}`}
                />
                <div
                  className={`absolute left-30 top-12 h-10 w-10 rounded-full ${style.bubble}`}
                />
                <div
                  className={`absolute -right-25 top-16 h-50 w-50 rounded-full ${style.bubble}`}
                />
                <div
                  className={`absolute bottom-28 -left-8.75 h-17.5 w-17.5 rounded-full ${style.bubble}`}
                />

                <div className="relative z-10 flex h-full flex-col">
                  <div className="mb-12">
                    <div className="relative h-21.5 w-21.5">
                      {iconUrl && (
                        <Image
                          src={iconUrl}
                          alt={item.icon?.alternativeText || item.titre}
                          fill
                          className="object-contain"
                        />
                      )}
                    </div>
                  </div>

                  <h3 className="text-[28px] font-bold leading-[1.15] text-text-body">
                    {item.titre}
                  </h3>

                  <p className="mt-6 text-[24px] leading-[1.5] text-black ">
                    {item.description}
                  </p>

                  <div className="mt-auto pt-10">
                    <Link
                      href={item.cta?.link || "#"}
                      className={`inline-flex items-center gap-3 rounded-full px-7 py-4 text-[16px] font-semibold text-black transition lg:text-[20px] ${style.button}`}
                    >
                      {item.cta?.text}
                      <ArrowRightCircle className="h-6 w-6" />
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}